// ── Report content section — the report body, rendered SAFELY (Wave 7) ──────
// SANITIZED-HTML RELEASE GATE (blueprint §6 gate #7): report content from the
// backend is PLAIN TEXT + STRING ARRAYS — `summary` (string|null) + `highlights`
// (string[]) + `actionItems` (string[]). The backend toReport mapper does NOT
// populate any HTML field (report_html is never set server-side), so we render
// with React's DEFAULT ESCAPING — NEVER dangerouslySetInnerHTML. Even if an HTML
// artifact were ever introduced, it would have to be sanitized before render; we
// assert (grep + test) that no unsanitized dangerouslySetInnerHTML exists in the
// reports UI. There is intentionally NO raw-HTML injection path here.
//
// Content is shown ONLY when the report is ready/completed. A not-ready / failed
// report shows its STATUS posture (handled by the page), never a fabricated body.

import { Alert, Module, ModuleBody, ModuleHead } from "@vjal/ui";

import type { CoachingArtifactDto } from "../../../services/coaching-dtos";

export function ReportContentSection({ report }: { report: CoachingArtifactDto }) {
  const summary = (report.summary ?? "").trim();
  const highlights = report.highlights ?? [];
  const actionItems = report.action_items ?? [];
  const hasBody = summary.length > 0 || highlights.length > 0 || actionItems.length > 0;

  return (
    <Module span={8}>
      <ModuleHead title="Report" subtitle="Summary, highlights, and action items for this report." />
      <ModuleBody className="space-y-5" data-testid="report-content">
        {hasBody ? (
          <>
            {summary.length > 0 ? (
              <section aria-label="Report summary">
                <h2 className="m-0 text-sm font-semibold text-strong">Summary</h2>
                {/* Plain text — React escapes it. No dangerouslySetInnerHTML. */}
                <p className="m-0 mt-1 whitespace-pre-line text-sm leading-6 text-ink-body">{summary}</p>
              </section>
            ) : null}
            {highlights.length > 0 ? (
              <section aria-label="Report highlights">
                <h2 className="m-0 text-sm font-semibold text-strong">Highlights</h2>
                <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-sm leading-6 text-ink-body">
                  {highlights.map((highlight, index) => (
                    <li key={`${index}-${highlight}`}>{highlight}</li>
                  ))}
                </ul>
              </section>
            ) : null}
            {actionItems.length > 0 ? (
              <section aria-label="Report action items">
                <h2 className="m-0 text-sm font-semibold text-strong">Action items</h2>
                <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-sm leading-6 text-ink-body">
                  {actionItems.map((item, index) => (
                    <li key={`${index}-${item}`}>{item}</li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        ) : (
          <Alert variant="info">
            This report doesn't have an in-dashboard body to show yet. Its content will appear here once it's generated.
          </Alert>
        )}
      </ModuleBody>
    </Module>
  );
}
